<template>
  <div class="services-form">
    <Form
      v-slot="$form"
      :initial-values="initialValues"
      :resolver="resolver"
      :validate-on-value-update="true"
      :validate-on-blur="true"
      class="services-form__form"
      @submit="onFormSubmit"
    >
      <!-- Validation Summary for Files/Status -->
      <Message
        v-if="attemptedSubmit && showFileValidationError"
        severity="error"
        icon="pi pi-exclamation-triangle"
        class="services-form__validation-summary"
      >
        <div class="services-form__validation-content">
          <strong>Kan ikke lagre servicerapport</strong>
          <p>Vedlegg er påkrevd for å markere som fullført</p>
        </div>
      </Message>

      <!-- Description Section -->
      <div class="services-form__field-group">
        <label for="description" class="services-form__label">
          Beskrivelse <span class="services-form__required">*</span>
        </label>
        <Textarea
          name="description"
          rows="3"
          :max-rows="6"
          placeholder="Beskriv hvilke arbeidsoppgaver som ble utført..."
          :disabled="isSubmitting"
          :invalid="$form.description?.invalid"
          class="services-form__textarea"
          auto-resize
        />
        <Message
          v-if="$form.description?.invalid"
          severity="error"
          size="small"
          variant="simple"
          class="services-form__error"
        >
          {{ $form.description.error?.message }}
        </Message>
      </div>

      <!-- Date Completed Section -->
      <div class="services-form__field-group">
        <label for="serviceDate" class="services-form__label">
          Dato utført <span class="services-form__required">*</span>
        </label>
        <DatePicker
          name="serviceDate"
          :disabled="isSubmitting"
          :invalid="$form.serviceDate?.invalid"
          date-format="dd.mm.yy"
          class="services-form__date-picker"
          placeholder="Velg dato for utført service"
        />
        <Message
          v-if="$form.serviceDate?.invalid"
          severity="error"
          size="small"
          variant="simple"
          class="services-form__error"
        >
          {{ $form.serviceDate.error?.message }}
        </Message>
      </div>

      <!-- Next Service Date Section -->
      <div class="services-form__field-group">
        <label for="nextServiceDate" class="services-form__label">
          Måned for neste service (valgfritt)
        </label>
        <DatePicker
          name="nextServiceDate"
          :disabled="isSubmitting"
          :invalid="$form.nextServiceDate?.invalid"
          :min-date="minNextServiceDate"
          date-format="mm-yy"
          view="month"
          class="services-form__date-picker"
          placeholder="Velg måned for neste service (mm-åå)"
        />
        <Message
          v-if="$form.nextServiceDate?.invalid"
          severity="error"
          size="small"
          variant="simple"
          class="services-form__error"
        >
          {{ $form.nextServiceDate.error?.message }}
        </Message>
      </div>

      <!-- File Upload Section -->
      <div class="services-form__field-group">
        <label class="services-form__label"> Vedlegg </label>
        <!-- Hidden file input for manual triggering -->
        <input
          ref="hiddenFileInput"
          type="file"
          :multiple="true"
          :accept="'.pdf,.jpg,.jpeg,.png,.doc,.docx'"
          style="display: none"
          @change="onHiddenFileInputChange"
        />
        <FileUpload
          ref="fileUploadRef"
          name="files"
          :custom-upload="true"
          :multiple="true"
          :file-limit="5"
          :max-file-size="10485760"
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          :disabled="isSubmitting"
          class="services-form__file-upload"
          @select="onFileSelect"
          @remove="onFileRemove"
          @clear="onFilesClear"
        >
          <template #header>
            <div class="services-form__upload-header">
              <i class="pi pi-cloud-upload services-form__upload-icon" />
              <span>Klikk for å laste opp eller dra og slipp</span>
              <BaseButton
                v-if="selectedFiles.length > 0"
                text="Fjern alle"
                :icon="CloseIcon"
                variant="tertiary"
                :uppercase="true"
                size="xs"
                @click="onFilesClear"
              />
              <BaseButton
                v-if="selectedFiles.length > 0 && selectedFiles.length < 5"
                text="Velg filer"
                :icon="AddIcon"
                variant="tertiary"
                :uppercase="true"
                size="xs"
                @click="triggerFileInput"
              />
            </div>
          </template>
          <template #content>
            <div
              v-if="selectedFiles.length === 0"
              class="services-form__upload-empty"
            >
              <p>Støttede formater: PDF, JPG, PNG, DOC, DOCX</p>
              <p>Maksimal filstørrelse: 10 MB per fil</p>
              <p>
                <strong>Merk:</strong> Minst ett vedlegg kreves for å markere
                som fullført
              </p>
              <BaseButton
                text="Velg filer"
                :icon="AddIcon"
                variant="tertiary"
                :uppercase="true"
                class="services-form__upload-button"
                @click="triggerFileInput"
              />
            </div>
            <div v-else class="services-form__upload-files">
              <div
                v-for="file in selectedFiles"
                :key="file.name"
                class="services-form__upload-file"
              >
                <i class="pi pi-file services-form__upload-file-icon" />
                <span class="services-form__upload-file-name">{{
                  file.name
                }}</span>
                <span class="services-form__upload-file-size">
                  ({{ formatFileSize(file.size) }})
                </span>
                <BaseButton
                  :icon="CloseIcon"
                  variant="tertiary"
                  size="xs"
                  :rounded="true"
                  shape="circle"
                  class="services-form__upload-file-delete"
                  @click="removeFile(file)"
                />
              </div>
            </div>
          </template>
        </FileUpload>
      </div>

      <!-- Status Section -->
      <div class="services-form__field-group">
        <label class="services-form__label">Status</label>
        <div class="services-form__status-options">
          <div class="services-form__status-option">
            <RadioButton
              v-model="currentStatus"
              name="status"
              value="Pending"
              :disabled="isSubmitting"
            />
            <label class="services-form__status-label">Pågående</label>
          </div>
          <div class="services-form__status-option">
            <RadioButton
              v-model="currentStatus"
              name="status"
              value="Completed"
              :disabled="isSubmitting || selectedFiles.length === 0"
            />
            <label
              class="services-form__status-label"
              :class="{
                'services-form__status-label--disabled':
                  selectedFiles.length === 0,
              }"
            >
              Fullført
              <span
                v-if="selectedFiles.length === 0"
                class="services-form__status-note"
              >
                (krever vedlegg)
              </span>
            </label>
          </div>
        </div>
      </div>

      <!-- Form Actions -->
      <div class="services-form__actions">
        <BaseButton
          text="Avbryt"
          variant="tertiary"
          :uppercase="true"
          :disabled="isSubmitting"
          @click="handleCancel"
        />
        <BaseButton
          :text="editingRecord ? 'Oppdater' : 'lagre servicerapport'"
          type="submit"
          :uppercase="true"
          :is-loading="isSubmitting"
        />
      </div>

      <!-- Future Date Confirmation Dialog -->
      <Dialog
        :visible="showFutureDateDialog"
        modal
        header="Bekreft fullføring"
        :style="{ width: '450px' }"
        :closable="false"
        @update:visible="showFutureDateDialog = $event"
      >
        <div class="services-form__confirm-content">
          <i class="pi pi-info-circle services-form__confirm-icon" />
          <div>
            <p>
              Fullføringsdatoen er satt til
              {{ formatDate(pendingFormData?.serviceDate) }}. Dette vil bli
              oppdatert til dagens dato ({{ formatDate(new Date()) }}).
              Fortsette?
            </p>
          </div>
        </div>
        <template #footer>
          <BaseButton
            text="Avbryt"
            variant="tertiary"
            :uppercase="true"
            @click="showFutureDateDialog = false"
          />
          <BaseButton
            text="Bekreft og fullfør"
            :uppercase="true"
            @click="handleFutureDateConfirm"
          />
        </template>
      </Dialog>
    </Form>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { Form } from "@primevue/forms";
import Textarea from "primevue/textarea";
import DatePicker from "primevue/datepicker";
import RadioButton from "primevue/radiobutton";
import FileUpload from "primevue/fileupload";
import Dialog from "primevue/dialog";
import Message from "primevue/message";
import BaseButton from "@/components/Base/BaseButton/BaseButton.vue";
import { useServiceRecordsApi } from "@/services/api/useServiceRecordsApi";
import type {
  ServiceRecord,
  CreateServiceRecordDto,
  UpdateServiceRecordDto,
  FileSelectEvent,
} from "@/types/service-records";
import { getMessageAttachmentDto } from "@/utilities/fileUtils";
import AddIcon from "@/assets/img/Plus.svg";
import CloseIcon from "@/assets/img/Close.svg";

interface Props {
  serviceOfferId: number;
  tenderId: number;
  customerOrgNumber: string;
  supplierId: number;
  editingRecord?: ServiceRecord | null;
}

interface FormData {
  description: string;
  serviceDate: Date | null;
  nextServiceDate: Date | null;
  status: "Pending" | "Completed";
}

interface Emits {
  (e: "hide"): void;
  (e: "new-service-record"): void;
  (e: "update-service-record"): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

// Composables
const { createServiceRecord, updateServiceRecord, markServiceRecordComplete } =
  useServiceRecordsApi();

// Reactive state
const selectedFiles = ref<File[]>([]);
const isSubmitting = ref(false);
const attemptedSubmit = ref(false);
const showFutureDateDialog = ref(false);
const pendingFormData = ref<FormData | null>(null);
const fileUploadRef = ref();
const hiddenFileInput = ref<HTMLInputElement>();
const currentStatus = ref<"Pending" | "Completed">(
  props.editingRecord?.serviceState || props.editingRecord?.status || "Pending",
);

// Form configuration
const initialValues: FormData = {
  description: props.editingRecord?.comment || "",
  serviceDate: props.editingRecord?.serviceDate
    ? new Date(props.editingRecord.serviceDate)
    : null,
  nextServiceDate: props.editingRecord?.nextServiceDate
    ? new Date(props.editingRecord.nextServiceDate)
    : null,
  status:
    props.editingRecord?.serviceState ||
    props.editingRecord?.status ||
    "Pending",
};

// Load existing files if editing
if (props.editingRecord?.attachments) {
  // Note: In a real implementation, you'd need to convert the attachment URLs back to File objects
  // This is a simplified approach
}

const resolver = ({ values }: { values: Record<string, unknown> }) => {
  const errors: Record<string, Array<{ message: string }>> = {};

  // Description validation
  const description = values.description as string | undefined;
  if (!description || description.trim().length === 0) {
    errors.description = [{ message: "Beskrivelse er påkrevd" }];
  } else if (description.length > 500) {
    errors.description = [
      { message: "Beskrivelsen kan ikke være lengre enn 500 tegn" },
    ];
  }

  // Service date validation - now required
  const serviceDate = values.serviceDate as Date | null;
  if (!serviceDate) {
    errors.serviceDate = [{ message: "Dato utført er påkrevd" }];
  }

  // Next service date validation
  // const nextServiceDate = values.nextServiceDate as Date | null;
  // if (serviceDate && nextServiceDate) {
  //   // For month-only comparison, we compare year and month
  //   const serviceDateMonth =
  //     serviceDate.getFullYear() * 12 + serviceDate.getMonth();
  //   const nextServiceDateMonth =
  //     nextServiceDate.getFullYear() * 12 + nextServiceDate.getMonth();

  //   if (nextServiceDateMonth < serviceDateMonth) {
  //     errors.nextServiceDate = [
  //       {
  //         message: "Måned for neste service kan ikke være før fullføringsdato",
  //       },
  //     ];
  //   } else if (nextServiceDateMonth <= serviceDateMonth) {
  //     errors.nextServiceDate = [
  //       {
  //         message:
  //           "Måned for neste service må være minst én måned etter fullføringsdato",
  //       },
  //     ];
  //   }
  // }

  return { errors };
};

// Computed properties
const serviceDate = computed(() => {
  // This should be bound to the form values, simplified for now
  return initialValues.serviceDate || undefined;
});

const minNextServiceDate = computed(() => {
  if (!serviceDate.value) return undefined;
  const oneMonthLater = new Date(serviceDate.value);
  oneMonthLater.setMonth(oneMonthLater.getMonth() + 1);
  // Set to first day of the month for month view
  oneMonthLater.setDate(1);
  return oneMonthLater;
});

const showFileValidationError = computed(() => {
  // Only show file validation error when user is trying to mark as "Completed" without files
  return (
    selectedFiles.value.length === 0 &&
    attemptedSubmit.value &&
    currentStatus.value === "Completed"
  );
});

// Watchers
watch(selectedFiles, (newFiles) => {
  // Reset validation error when files are added
  if (newFiles.length > 0 && attemptedSubmit.value) {
    attemptedSubmit.value = false;
  }

  // If no files and status is "Completed", switch back to "Pending"
  if (newFiles.length === 0 && currentStatus.value === "Completed") {
    currentStatus.value = "Pending";
  }
});

// Remove this computed property since we're not using actual form state
// The validation is properly handled by the resolver function

// File handling
const onFileSelect = (event: FileSelectEvent) => {
  // Prevent duplicate files by checking if they already exist
  const newFiles = event.files.filter(
    (newFile) =>
      !selectedFiles.value.some(
        (existingFile) =>
          existingFile.name === newFile.name &&
          existingFile.size === newFile.size &&
          existingFile.lastModified === newFile.lastModified,
      ),
  );

  // Respect the file limit (5 files max)
  const remainingSlots = 5 - selectedFiles.value.length;
  const filesToAdd = newFiles.slice(0, remainingSlots);

  selectedFiles.value = [...selectedFiles.value, ...filesToAdd];
};

const onFileRemove = (event: { file: File }) => {
  selectedFiles.value = selectedFiles.value.filter(
    (file) => file.name !== event.file.name,
  );
};

const onFilesClear = () => {
  selectedFiles.value = [];
};

const removeFile = (fileToRemove: File) => {
  selectedFiles.value = selectedFiles.value.filter(
    (file) => file !== fileToRemove,
  );
};

const onHiddenFileInputChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target.files) {
    const fileArray = Array.from(target.files);
    const mockEvent = { files: fileArray };
    onFileSelect(mockEvent as FileSelectEvent);
    // Clear the input so the same file can be selected again
    target.value = "";
  }
};

const triggerFileInput = () => {
  // Use our hidden file input for reliable file selection
  if (hiddenFileInput.value) {
    hiddenFileInput.value.click();
  }
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
};

const formatDate = (date: Date | null | undefined): string => {
  if (!date) return "";
  return new Intl.DateTimeFormat("no-NO", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
};

// Utility function to scroll up the page by 200 pixels
const scrollUp = () => {
  window.scrollBy({
    top: -300,
    behavior: "smooth",
  });
};

// Form handlers
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onFormSubmit = async (submitEvent: any) => {
  attemptedSubmit.value = true;

  const valid = submitEvent.valid;

  if (!valid) {
    console.log("Form validation failed", submitEvent.errors);
    return;
  }

  // Extract values from submitEvent.states with better fallback
  const states = submitEvent.states || {};
  const values: FormData = {
    description: states?.description?.value?.trim() || "",
    serviceDate: states?.serviceDate?.value || null,
    nextServiceDate: states?.nextServiceDate?.value || null,
    status: currentStatus.value, // Use tracked status instead of form state
  };

  // Basic validation - description and serviceDate are required
  if (!values.description?.trim()) {
    console.error("Description validation failed in submit handler");
    return;
  }

  if (!values.serviceDate) {
    console.error("Service date validation failed in submit handler");
    return;
  }

  // Check if trying to mark as completed without files
  if (values.status === "Completed" && selectedFiles.value.length === 0) {
    console.error("Cannot mark as completed without attachments");
    return;
  }

  // Check if trying to mark as completed with future date
  if (
    values.status === "Completed" &&
    values.serviceDate &&
    values.serviceDate > new Date()
  ) {
    pendingFormData.value = values;
    showFutureDateDialog.value = true;
    return;
  }

  handleFormSubmit(values);
};

const handleFormSubmit = async (values: FormData) => {
  isSubmitting.value = true;

  try {
    // Convert dates to ISO string format - serviceDate is now required
    const serviceDate = values.serviceDate!.toISOString().split("T")[0];

    // For nextServiceDate, format as month-year (YYYY-MM-01)
    // Since we're using month view, we set it to the first day of the month
    let nextServiceDate = null;
    if (values.nextServiceDate) {
      const nextDate = new Date(values.nextServiceDate);
      const year = nextDate.getFullYear();
      const month = String(nextDate.getMonth() + 1).padStart(2, "0");
      nextServiceDate = `${year}-${month}-01`;
    }

    // Convert File objects to FileAttachment DTOs
    const attachments = await Promise.all(
      selectedFiles.value.map((file) => getMessageAttachmentDto(file)),
    );

    // Remember if user selected "Completed" status
    const shouldMarkComplete = values.status === "Completed";

    if (props.editingRecord) {
      // Update existing record
      const updateData: UpdateServiceRecordDto = {
        serviceDate: serviceDate,
        nextServiceDate: nextServiceDate,
        comment: values.description,
        attachments,
      };

      await updateServiceRecord(
        props.editingRecord.id,
        props.supplierId,
        props.customerOrgNumber,
        updateData,
      );

      // If user selected "Completed", mark the record as complete
      if (shouldMarkComplete) {
        await markServiceRecordComplete(
          props.editingRecord.id,
          props.supplierId,
          props.customerOrgNumber,
        );
      }

      // Only emit success event if update was successful
      emit("update-service-record");
      attemptedSubmit.value = false; // Reset validation state on success
    } else {
      // Create new record
      const createData: CreateServiceRecordDto = {
        serviceDate: serviceDate,
        nextServiceDate: nextServiceDate,
        comment: values.description,
        attachments,
      };

      const createdRecord = await createServiceRecord(
        props.supplierId,
        props.customerOrgNumber,
        props.serviceOfferId,
        createData,
      );

      // If user selected "Completed", mark the newly created record as complete
      if (shouldMarkComplete && createdRecord?.data?.id) {
        await markServiceRecordComplete(
          createdRecord.data.id,
          props.supplierId,
          props.customerOrgNumber,
        );
      }

      // Only emit success event if creation was successful
      emit("new-service-record");
      attemptedSubmit.value = false; // Reset validation state on success
    }
  } catch (error) {
    console.error("Failed to save service record:", error);
    scrollUp();
    // Don't emit success events when there's an error
  } finally {
    isSubmitting.value = false;
  }
};

const handleFutureDateConfirm = () => {
  showFutureDateDialog.value = false;

  if (pendingFormData.value) {
    // Update the service date to today
    const updatedData = {
      ...pendingFormData.value,
      serviceDate: new Date(),
    };
    handleFormSubmit(updatedData);
  }
};

const handleCancel = () => {
  emit("hide");
  setTimeout(() => {
    scrollUp();
  }, 1000);
};
</script>

<style lang="scss" scoped>
.services-form {
  padding: 0.5rem;
  @media (max-width: $bp-sm) {
    padding: 1rem;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  &__validation-summary {
    margin-bottom: 1rem;
  }

  &__validation-content {
    strong {
      display: block;
      margin-bottom: 0rem;
    }

    p {
      margin-bottom: 0rem;
    }
  }

  &__validation-list {
    margin: 0;
    padding-left: 1.5rem;

    li {
      margin-bottom: 0.25rem;
    }
  }

  &__field-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__label {
    font-weight: 600;
    color: #1d1d1d;
    font-size: 0.875rem;
    text-transform: uppercase;
    letter-spacing: 0.025em;
  }

  &__required {
    color: $color-error;
  }

  &__textarea,
  &__date-picker {
    width: 100%;
  }

  &__error {
    margin-top: 0.25rem;
  }

  &__file-upload {
    width: 100%;
  }

  &__upload-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 500;
  }

  &__upload-icon {
    color: $color-primary;
  }

  &__upload-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    color: $color-text-secondary;
    padding: 2rem;
  }

  &__upload-button {
    margin-top: 1rem;
    width: fit-content;
  }

  &__upload-files {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 1rem;
  }

  &__upload-file {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem;
    background: $color-primary-lighter;
    border-radius: 6px;

    &-icon {
      color: $color-primary;
      flex-shrink: 0;
    }

    &-name {
      flex: 1;
      font-weight: 500;
      color: $color-primary-darker;
    }

    &-size {
      color: $color-text-secondary;
      font-size: 0.875rem;
    }

    &-delete {
      flex-shrink: 0;
      color: $color-black;

      &:hover {
        background-color: $color-error-light;
      }
    }
  }

  &__status-options {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  &__status-option {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  &__status-label {
    font-weight: 500;
    color: $color-text-primary;
    cursor: pointer;

    &--disabled {
      color: $color-text-secondary;
      cursor: not-allowed;
    }
  }

  &__status-note {
    font-size: 0.875rem;
    color: $color-text-secondary;
    font-weight: 400;
  }

  &__actions {
    display: flex;
    gap: 1rem;
    justify-content: flex-end;
    margin-top: 1rem;

    @media (max-width: $bp-sm) {
      flex-direction: column;
    }
  }

  &__confirm-content {
    display: flex;
    gap: 1rem;
    align-items: flex-start;

    p {
      margin: 0;
      line-height: 1.5;
    }
  }

  &__confirm-icon {
    color: $color-info;
    font-size: 1.25rem;
    flex-shrink: 0;
    margin-top: 0.125rem;
  }
}
</style>
